import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Mail, Globe, Instagram, Twitter, AtSign } from "lucide-react";

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white border border-neutral-200 p-5">
      <div className="overline text-neutral-500 text-[10px]">{label}</div>
      <div className="font-serif text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

const statusColor = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  archived: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

const orderStatusColor = {
  pending: "bg-amber-50 text-amber-700",
  printing: "bg-blue-50 text-blue-700",
  shipped: "bg-purple-50 text-purple-700",
  delivered: "bg-emerald-50 text-emerald-700",
  cancelled: "bg-neutral-100 text-neutral-500",
};

export default function AdminUserDetail() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/users/${user_id}`).then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, [user_id]);

  if (!data) return <div className="overline text-neutral-400 py-16 text-center">Loading...</div>;

  const u = data.user;
  const rd = data.role_data || {};
  const isSelf = me?.user_id === u.user_id;

  const changeRole = async (role) => {
    setBusy(true);
    try { await api.patch(`/admin/users/${u.user_id}/role`, { role }); toast.success("Rol actualizado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleDisable = async () => {
    const action = u.disabled ? "habilitar" : "deshabilitar";
    if (!window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} a ${u.name}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}/disable`);
      toast.success(data.disabled ? "Usuario deshabilitado" : "Usuario habilitado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar definitivamente a ${u.name}? Esta acción no se puede deshacer.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Usuario eliminado");
      navigate("/admin/users");
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <div data-testid="admin-user-detail">
      <button onClick={() => navigate("/admin/users")} className="inline-flex items-center gap-2 overline text-xs text-neutral-500 hover:text-black mb-6" data-testid="back-to-users">
        <ArrowLeft size={14} /> Volver a usuarios
      </button>

      <div className="bg-white border border-neutral-200 p-6 lg:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
          <div className="flex-shrink-0">
            {u.picture ? (
              <img src={fileUrl(u.picture)} alt={u.name} className="w-24 h-24 rounded-full object-cover bg-neutral-100" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-neutral-100 flex items-center justify-center font-serif text-3xl text-neutral-400">
                {u.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-serif text-3xl lg:text-4xl font-light" data-testid="user-name">{u.name}</h1>
              <span className="overline text-xs px-2 py-1 border border-neutral-300 bg-neutral-50" data-testid="user-role-badge">{u.role}</span>
              {u.disabled && (
                <span className="overline text-xs px-2 py-1 border border-red-300 bg-red-50 text-red-700" data-testid="disabled-badge">Deshabilitado</span>
              )}
            </div>
            <div className="text-sm text-neutral-600 mt-2 flex items-center gap-2"><Mail size={12} /> <a href={`mailto:${u.email}`} className="hover:underline">{u.email}</a></div>
            {u.bio && <p className="text-sm text-neutral-700 mt-4 max-w-2xl leading-relaxed">{u.bio}</p>}
            <div className="flex flex-wrap gap-4 mt-3 text-xs text-neutral-500">
              {u.website && <a href={u.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-black"><Globe size={12} /> Web</a>}
              {u.instagram && <a href={`https://instagram.com/${u.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-black"><Instagram size={12} /> {u.instagram}</a>}
              {u.twitter && <a href={`https://twitter.com/${u.twitter.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-black"><Twitter size={12} /> {u.twitter}</a>}
              {u.tiktok && <span className="flex items-center gap-1"><AtSign size={12} /> {u.tiktok}</span>}
              <span>Creado: {new Date(u.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {!isSelf && (
          <div className="mt-6 pt-6 border-t border-neutral-100 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="overline text-xs text-neutral-500">Rol:</span>
              <select value={u.role} disabled={busy} onChange={(e) => changeRole(e.target.value)} className="border border-neutral-300 px-2 py-1.5 text-xs bg-white" data-testid="role-select">
                {["customer", "artist", "referrer", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button onClick={toggleDisable} disabled={busy} className={`overline text-xs px-4 py-2 border ${u.disabled ? "border-emerald-600 text-emerald-700 hover:bg-emerald-50" : "border-amber-500 text-amber-700 hover:bg-amber-50"}`} data-testid="disable-toggle">
              {u.disabled ? "Habilitar cuenta" : "Deshabilitar cuenta"}
            </button>
            <button onClick={remove} disabled={busy} className="overline text-xs px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50" data-testid="delete-user">
              Eliminar usuario
            </button>
          </div>
        )}
        {isSelf && (
          <div className="mt-6 pt-6 border-t border-neutral-100 text-xs text-neutral-500">Esta es tu propia cuenta. No puedes deshabilitarla ni eliminarla.</div>
        )}
      </div>

      {u.role === "artist" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <Stat label="Obras" value={rd.total_works} sub={`${rd.approved} aprob · ${rd.pending} pend · ${rd.archived} arch`} />
            <Stat label="Vistas totales" value={rd.total_views} />
            <Stat label="Unidades vendidas" value={rd.units_sold} />
            <Stat label="Ingresos" value={`€${rd.revenue?.toFixed(2) || "0.00"}`} />
            <Stat label="Neto comprometido" value={`€${rd.net_earned?.toFixed(2) || "0.00"}`} />
          </div>

          <h2 className="overline text-neutral-500 mb-3">Obras del artista ({rd.artworks?.length || 0})</h2>
          {(rd.artworks || []).length === 0 ? (
            <div className="bg-white border border-neutral-200 py-12 text-center overline text-neutral-400 mb-8">
              Sin obras todavía
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8" data-testid="artworks-grid">
              {rd.artworks.map((w) => {
                const sizes = w.sizes || [];
                const retails = sizes.map((s) => s.price || 0);
                const minPrice = retails.length ? Math.min(...retails) : 0;
                const maxPrice = retails.length ? Math.max(...retails) : 0;
                return (
                  <Link
                    key={w.id}
                    to={`/artwork/${w.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="group bg-white border border-neutral-200 hover:border-neutral-400 transition-colors block"
                    data-testid={`detail-work-${w.id}`}
                  >
                    <div className="aspect-[4/5] bg-neutral-100 overflow-hidden">
                      <img
                        src={fileUrl(w.image_path)}
                        alt={w.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`overline text-[10px] px-2 py-0.5 border ${statusColor[w.status] || ""}`}>{w.status}</span>
                        <span className="overline text-[10px] text-neutral-400">{w.views || 0} vistas</span>
                      </div>
                      <h3 className="font-serif text-base mt-2 leading-tight line-clamp-2">{w.title}</h3>
                      <div className="text-xs text-neutral-500 mt-1">{w.category}</div>
                      {retails.length > 0 && (
                        <div className="text-xs text-neutral-700 mt-2">
                          {retails.length > 1 ? `€${minPrice.toFixed(2)} – €${maxPrice.toFixed(2)}` : `€${minPrice.toFixed(2)}`}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <h2 className="overline text-neutral-500 mb-3">Pedidos con sus obras</h2>
          <OrdersTable orders={rd.recent_orders} />
        </>
      )}

      {u.role === "referrer" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <Stat label="Clicks" value={rd.clicks} />
            <Stat label="Pedidos" value={rd.orders_count} />
            <Stat label="Unidades" value={rd.units_sold} />
            <Stat label="Ventas atribuidas" value={`€${rd.sales_total?.toFixed(2) || "0.00"}`} />
            <Stat label="Comisión 10%" value={`€${rd.commission_earned?.toFixed(2) || "0.00"}`} />
          </div>
          <h2 className="overline text-neutral-500 mb-3">Pedidos referidos</h2>
          <OrdersTable orders={rd.recent_orders} />
        </>
      )}

      {u.role === "customer" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            <Stat label="Pedidos" value={rd.orders_count} />
            <Stat label="Gasto total" value={`€${rd.total_spent?.toFixed(2) || "0.00"}`} />
            <Stat label="Email" value={<span className="text-base font-sans font-normal break-all">{u.email}</span>} />
          </div>
          <h2 className="overline text-neutral-500 mb-3">Pedidos</h2>
          <OrdersTable orders={rd.recent_orders} />
        </>
      )}

      {u.role === "admin" && (
        <div className="bg-white border border-neutral-200 p-6 text-sm text-neutral-600">
          Cuenta de administrador. Sin métricas asociadas.
        </div>
      )}
    </div>
  );
}

function OrdersTable({ orders }) {
  if (!orders || orders.length === 0) {
    return <div className="bg-white border border-neutral-200 py-8 text-center overline text-neutral-400">—</div>;
  }
  return (
    <div className="bg-white border border-neutral-200 overflow-x-auto">
      <table className="brutal-table w-full">
        <thead>
          <tr>
            <th>N°</th>
            <th>Cliente</th>
            <th>Items</th>
            <th>Total</th>
            <th>Estado</th>
            <th>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} data-testid={`detail-order-${o.id}`}>
              <td className="font-mono text-xs">{o.id.slice(0, 8)}</td>
              <td>
                <div className="text-sm">{o.shipping_name}</div>
                <div className="text-xs text-neutral-500">{o.shipping_email}</div>
              </td>
              <td>
                {o.items.map((it, idx) => (
                  <div key={idx} className="text-xs">{it.title}{it.size_label ? ` · ${it.size_label}` : ""} × {it.quantity}</div>
                ))}
              </td>
              <td className="font-serif">€{o.total.toFixed(2)}</td>
              <td><span className={`overline text-[10px] px-2 py-1 ${orderStatusColor[o.status] || ""}`}>{o.status}</span></td>
              <td className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
